import { useEffect, useState } from 'react';
import useToast from '@hooks/useToast';

import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import { Stack, Typography } from '@mui/material';

import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import { urls } from '@api/urls.ts';
import { CompanyMember } from '@app/types/user/company';
import MainCard from '@components/ui/cards/MainCard';

import MainTable from '@components/MainTable';
import MainTablePagination from '@components/MainTable/MainTablePagination';
import { useTanStackTable } from '@hooks/useTanStackTable';
import InviteUserBecomeMemberModal from '@features/settings-users/components/InviteUserBecomeMemberModal';
import { SettingsUsersTableMeta } from '@app/types/overrides/table-meta';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { IconUserCheck, IconUserMinus } from '@tabler/icons-react';
import {
  activateCompanyUserAccountWithAdmin,
  deleteCompanyMember,
  inviteUserBecomeCompanyMember,
  updateAdminRoleForCompanyMember,
  updateAttorneyRoleForCompanyMember,
} from '@api/user/apiService';
import createSettingUsersColumns from './columns';

function SettingsUsersTable() {
  const [isInviteMemberModalOpen, setIsInviteMemberModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const {
    showDeleteConfirmation,
    showAlertConfirmation,
    showConfirmationModal,
    closeModal,
  } = useConfirmationModal();

  const { data, mutate, isLoading } = useDataFetcher<CompanyMember[]>({
    url: urls.companyMembers.base,
  });

  const handleCloseInviteUserModal = () => {
    setIsInviteMemberModalOpen(false);
  };

  const handleChangeCompanyAccountStatus = (companyMember: CompanyMember) => {
    if (!companyMember) return;
    const status =
      companyMember.company_user_status === 'Active' ? 'Inactive' : 'Active';
    const status_message = status === 'Active' ? 'activated' : 'deactivated';

    console.log(
      '[SettingsUsersTable] Optimistically updating SWR cache for user:',
      companyMember.user_id
    );
    // Optimistically update SWR cache first, then the useEffect will sync it to the table
    mutate(
      (currentData) => {
        if (!currentData) return currentData;
        return currentData.map((u) =>
          u.user_id === companyMember.user_id
            ? { ...u, company_user_status: status }
            : u
        );
      },
      false // Don't revalidate yet
    );

    activateCompanyUserAccountWithAdmin(
      companyMember.user_id,
      { account_status: status },
      () => {
        showSuccess(
          'Success',
          `Company user account was successfully ${status_message}`
        );
        console.log('[SettingsUsersTable] API success, revalidating SWR cache');
        // Now revalidate to get the real server state
        mutate(undefined, true);
        closeModal();
      },
      (error) => {
        showError(
          'Error',
          error.response?.data?.message ??
            'Something went wrong while changing account status'
        );
        console.log(
          '[SettingsUsersTable] API error, reverting optimistic update'
        );
        // Revert the optimistic update by revalidating
        mutate(undefined, true);
        closeModal();
      }
    );
  };

  const handleDelete = (companyMember: CompanyMember) => {
    if (!companyMember) return;
    const targetId =
      companyMember.company_user_id != null
        ? companyMember.company_user_id
        : companyMember.user_id;

    // Optimistically update SWR cache
    mutate((currentData) => {
      if (!currentData) return currentData;
      return currentData.map((u) =>
        u.user_id === companyMember.user_id
          ? { ...u, company_user_status: 'Inactive' }
          : u
      );
    }, false);

    deleteCompanyMember(
      targetId,
      () => {
        showSuccess('Success', 'Company user was successfully deleted.');
        mutate(undefined, true); // Revalidate to get real server state
        closeModal();
      },
      (errorResponse) => {
        showError(
          'Error during deleting company member',
          errorResponse.data.message ??
            'Something went wrong while deleting company member'
        );
        mutate(undefined, true); // Revert optimistic update
        closeModal();
      }
    );
  };

  const handleResendInvitation = (userMember: CompanyMember) => {
    if (!userMember) return;

    // Optimistically update SWR cache
    mutate((currentData) => {
      if (!currentData) return currentData;
      return currentData.map((u) =>
        u.user_id === userMember.user_id
          ? { ...u, company_user_status: 'Invitation pending' }
          : u
      );
    }, false);

    inviteUserBecomeCompanyMember(
      {
        email: userMember.user_email,
        re_send_invitation: true,
      },
      () => {
        showSuccess(
          'Success',
          `Invitation for ${userMember.user_name} was re-sent successfully`
        );
        mutate(undefined, true); // Revalidate to get real server state
        closeModal();
      },
      (error) => {
        showError(
          'Error',
          error.response?.data?.message ??
            'Something went wrong while re-sending invitation'
        );
        mutate(undefined, true); // Revert optimistic update
        closeModal();
      }
    );
  };

  const handleUpdateMemberAdminRole = (userMember: CompanyMember) => {
    if (!userMember) return;

    // Optimistically update SWR cache
    mutate((currentData) => {
      if (!currentData) return currentData;
      return currentData.map((u) =>
        u.user_id === userMember.user_id
          ? { ...u, is_company_admin: !userMember.is_company_admin }
          : u
      );
    }, false);

    updateAdminRoleForCompanyMember(
      userMember.user_id,
      { is_company_admin: !userMember.is_company_admin },
      () => {
        showSuccess(
          'Success',
          `Admin role for ${userMember.user_name} was successfully changed`
        );
        mutate(undefined, true); // Revalidate to get real server state
        closeModal();
      },
      (error) => {
        showError(
          'Error',
          error.response?.data?.message ??
            'Something went wrong while changing admin role'
        );
        mutate(undefined, true); // Revert optimistic update
        closeModal();
      }
    );
  };

  const handleUpdateMemberAttorneyRole = (userMember: CompanyMember) => {
    if (!userMember) return;
    const next = !userMember.is_attorney;

    mutate((currentData) => {
      if (!currentData) return currentData;
      return currentData.map((u) =>
        u.user_id === userMember.user_id ? { ...u, is_attorney: next } : u
      );
    }, false);

    updateAttorneyRoleForCompanyMember(
      userMember.user_id,
      { is_attorney: next },
      () => {
        showSuccess(
          'Success',
          next
            ? `${userMember.user_name} is now marked as a lawyer`
            : `${userMember.user_name} is no longer marked as a lawyer`
        );
        mutate(undefined, true);
        closeModal();
      },
      (error) => {
        showError(
          'Error',
          error.response?.data?.message ??
            'Something went wrong while changing the lawyer role'
        );
        mutate(undefined, true);
        closeModal();
      }
    );
  };

  const { table, setData } = useTanStackTable<CompanyMember>({
    tableData: data || [],
    columnConfig: createSettingUsersColumns(),
    initialSorting: [{ id: 'user_name', desc: false }],
    options: {
      getRowId: (row) => String(row.user_id),
      meta: {
        handleActivateUser: (user: CompanyMember) => {
          showConfirmationModal({
            icon: <IconUserCheck />,
            title: 'Activate User',
            color: 'primary',
            cancelLabel: 'No, keep it deactivated',
            confirmLabel: 'Yes, activate',
            content: 'Are you sure you want to activate this account?',
            onConfirm: () => handleChangeCompanyAccountStatus(user),
          });
        },
        handleDeactivateUser: (user: CompanyMember) => {
          showConfirmationModal({
            icon: <IconUserMinus />,
            title: 'Deactivate User',
            color: 'error',
            cancelLabel: 'No, keep it activated',
            confirmLabel: 'Yes, deactivate',
            content: 'Are you sure you want to deactivate this account?',
            onConfirm: () => handleChangeCompanyAccountStatus(user),
          });
        },
        handleDeleteRow: (user: CompanyMember) => {
          showDeleteConfirmation(
            () => handleDelete(user),
            'Are you sure you want to delete this company member?'
          );
        },
        handleResendInvitation: (user: CompanyMember) => {
          showAlertConfirmation(
            'Hey, check this out!',
            'Are you sure you want to re-send the invitation email?',
            () => handleResendInvitation(user)
          );
        },
        handleUpdateMemberAdminRole: (user: CompanyMember) => {
          const action = user.is_company_admin
            ? 'remove admin role for this member'
            : 'make this user company admin';
          const content = `Are you sure you want to ${action}?`;
          showAlertConfirmation('Hey, check this out!', content, () =>
            handleUpdateMemberAdminRole(user)
          );
        },
        handleUpdateMemberAttorneyRole: (user: CompanyMember) => {
          const content = user.is_attorney
            ? `Remove the lawyer role from ${user.user_name}? They will no longer be able to approve or reject documents.`
            : `Mark ${user.user_name} as a lawyer? Lawyers can approve and reject documents for the firm.`;
          showAlertConfirmation('Lawyer role', content, () =>
            handleUpdateMemberAttorneyRole(user)
          );
        },
      } as SettingsUsersTableMeta<CompanyMember>,
    },
  });

  useEffect(() => {
    if (!data) return;
    console.log(
      '[SettingsUsersTable] SWR data updated, syncing to table:',
      data.length,
      'users'
    );
    setData(data);
  }, [data, setData]);

  return (
    <MainCard>
      <Stack gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h3">Users</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsInviteMemberModalOpen(true)}
            sx={{
              flexGrow: { xs: 1, sm: 0 },
            }}
          >
            Invite User
          </Button>
        </Stack>
        <Box>
          <MainTable table={table} isLoading={isLoading} />
          <MainTablePagination table={table} isLoading={isLoading} />
        </Box>
      </Stack>
      <InviteUserBecomeMemberModal
        open={isInviteMemberModalOpen}
        onClose={handleCloseInviteUserModal}
        onSuccess={() => {
          mutate();
          handleCloseInviteUserModal();
        }}
      />
    </MainCard>
  );
}

export default SettingsUsersTable;
